import { type RPPDomain } from '@retailer-platform/dashboard/core'
import {
  AdminNavEntryPointV2,
  AdminNavEntryPointV3,
  NavEntryPoint,
  NavEntryPointV3,
} from '@retailer-platform/dashboard/routing'
import { retailerFundedMarketingRouter } from './routing/retailerFundedMarketingRouter'
import { retailerFundedMarketingAdminRouter } from './routing/retailerFundedMarketingAdminRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import { retailerFundedMarketingHomeAccessControl } from './access-control/retailerFundedMarketingHomeAccess.configuration'
import { retailerFundedMarketingSegmentationAccessControl } from './access-control/retailerFundedMarketingTargetingAccess.configuration'
import { marketingAgentCalendarUnionAccessControl } from './access-control/marketingAgentCalendarAccess.configuration'
import { retailerFundedMarketingV2Router } from './routing/retailerFundedMarketingV2Router'
import {
  retailerFundedMarketingAdminAccessControl,
  retailerFundedMarketingAdminCustomCampaignAccessControl,
} from './access-control/retailerFundedMarketingAdminAccess.configuration'
import { globalCampaignsAdminRouter } from './routing/GlobalCampaignsAdminRouter'
import { campaignsAdminRouter } from './routing/campaignsAdminRouter'
import { marketingAgentAdminRouter } from './routing/marketingAgentAdminRouter'
import { retailerFundedMarketingWholesalerAccessControl } from './access-control/retailerFundedMarketingWholesalerAccess.configuration'
import { retailerFundedMarketingPartnerScopedRouter } from './routing/retailerFundedMarketingPartnerScopedRouter'
import { retailerFundedMarketingPartnerScopedSegmentRouter } from './routing/retailerFundedMarketingPartnerScopedSegmentRouter'
import { lifecycleCrmAccessControl } from './access-control/lifecycleCrmAccess.configuration'
import { sfpCampaignAndSegmentAccessControl } from './access-control/sfpCampaignAndSegmentAccess.configuration'
import { Permission } from './access-control/permissions'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'retailer-funded-marketing',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'retailer-funded-marketing-dashboard-tool',
  },
  routes,
  translations: { EN_US },
  lazyTranslations: {
    ES_ES: () => import('./translations/es-ES.json'),
    FR_FR: () => import('./translations/fr-FR.json'),
  },
  routers: {
    scoped: [retailerFundedMarketingV2Router, retailerFundedMarketingRouter],
    partnerScoped: [
      retailerFundedMarketingPartnerScopedRouter,
      retailerFundedMarketingPartnerScopedSegmentRouter,
    ],
    admin: [
      retailerFundedMarketingAdminRouter,
      globalCampaignsAdminRouter,
      campaignsAdminRouter,
      marketingAgentAdminRouter,
    ],
  },
  navigationEntries: [
    {
      attachToV2: NavEntryPoint.Marketing,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Create and manage retailer-funded marketing campaigns',
          labelId: 'retailerFundedMarketingDomain.campaigns.phaseFour.nav',
          route: 'retailer-funded-marketing-site-scoped-list',
          accessControl: retailerFundedMarketingHomeAccessControl,
          positionNavV2: 0,
          inToolPermissions: [
            {
              slug: Permission.OffersToolingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingViewAccess,
            },
            {
              slug: Permission.OffersToolingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingModifyAccess,
            },
            {
              slug: Permission.OffersToolingAdminAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingAdminAccess,
            },

            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsManualCRMAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsManualCRMAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsView,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsView,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsModify,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsModify,
            },

            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingAdminAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingAdminAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
            },

            {
              slug: Permission.RetailerFundedMarketingToolingPermissiveMode,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingToolingPermissiveMode,
            },
            {
              slug: Permission.RouletteApiAccessView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.RouletteApiAccessView,
            },
          ],
        },
        {
          type: 'item',
          llmDescription:
            "Manage Instacart's main marketplace (eg: instacart.com) marketing campaigns for product promotions",
          labelId: 'retailerFundedMarketingDomain.marketplaceCampaigns.nav',
          route: 'campaigns-marketplace-list',
          subRoutes: [
            'partner-campaigns-create-mp',
            'partner-campaigns-edit',
            'partner-campaigns-view',
          ],
          accessControl: retailerFundedMarketingWholesalerAccessControl,
          positionNavV2: 1,
          inToolPermissions: [
            {
              slug: Permission.OffersToolingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingViewAccess,
            },
            {
              slug: Permission.OffersToolingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingModifyAccess,
            },
            {
              slug: Permission.OffersToolingAdminAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingAdminAccess,
            },

            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsManualCRMAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsManualCRMAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsView,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsView,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsModify,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsModify,
            },

            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingAdminAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingAdminAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
            },

            {
              slug: Permission.RetailerFundedMarketingToolingPermissiveMode,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingToolingPermissiveMode,
            },
            {
              slug: Permission.RouletteApiAccessView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.RouletteApiAccessView,
            },
          ],
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage marketing campaigns exclusively for your custom Storefront Pro platform',
          labelId: 'retailerFundedMarketingDomain.storefrontProCampaigns.nav',
          route: 'campaigns-storefront-pro-list',
          accessControl: sfpCampaignAndSegmentAccessControl,
          positionNavV2: 2,
          inToolPermissions: [
            {
              slug: Permission.OffersToolingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingViewAccess,
            },
            {
              slug: Permission.OffersToolingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingModifyAccess,
            },
            {
              slug: Permission.OffersToolingAdminAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingAdminAccess,
            },

            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsManualCRMAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsManualCRMAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsView,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsView,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsModify,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsModify,
            },

            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingAdminAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingAdminAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
            },

            {
              slug: Permission.RetailerFundedMarketingToolingPermissiveMode,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingToolingPermissiveMode,
            },
            {
              slug: Permission.RouletteApiAccessView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.RouletteApiAccessView,
            },
          ],
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage customer segments specifically for Instacart marketplace (instacart.com) campaigns',
          labelId: 'retailerFundedMarketingDomain.marketplace.segmentation.nav',
          route: 'segment-marketplace-list',
          accessControl: retailerFundedMarketingWholesalerAccessControl,
          positionNavV2: 4,
          inToolPermissions: [
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingToolingPermissiveMode,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingToolingPermissiveMode,
            },
          ],
        },
        {
          type: 'item',
          llmDescription: 'Manage customer segments for Storefront Pro campaigns',
          labelId: 'retailerFundedMarketingDomain.storefrontPro.segmentation.nav',
          route: 'segment-storefront-pro-list',
          accessControl: sfpCampaignAndSegmentAccessControl,
          positionNavV2: 5,
          inToolPermissions: [
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingToolingPermissiveMode,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingToolingPermissiveMode,
            },
          ],
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage customer segments for all retailer-funded marketing campaigns',
          labelId: 'retailerFundedMarketingDomain.segmentation.nav',
          route: 'retailer-funded-marketing-segmentation-list',
          accessControl: retailerFundedMarketingSegmentationAccessControl,
          positionNavV2: 8,
          inToolPermissions: [
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingToolingPermissiveMode,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingToolingPermissiveMode,
            },
          ],
        },
        {
          type: 'item',
          llmDescription: 'Manage lifecycle marketing and customer relationship campaigns',
          labelId: 'retailerFundedMarketingDomain.lifecycleCrm.nav',
          route: 'partner-scoped-campaigns-lifecycle-crm',
          accessControl: lifecycleCrmAccessControl,
          positionNavV2: 1,
          inToolPermissions: [
            {
              slug: Permission.OffersToolingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingViewAccess,
            },
            {
              slug: Permission.OffersToolingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingModifyAccess,
            },
            {
              slug: Permission.OffersToolingAdminAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingAdminAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsManualCRMAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsManualCRMAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsView,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsView,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsModify,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsModify,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingAdminAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingAdminAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingToolingPermissiveMode,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingToolingPermissiveMode,
            },
            {
              slug: Permission.RouletteApiAccessView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.RouletteApiAccessView,
            },
          ],
        },
      ],
    },
    {
      attachToV3: NavEntryPointV3.Storefront,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'Create and manage customer segments for all retailer-funded marketing campaigns',
          // Site-scoped "Segments" page for Storefront-tier retailers. Same
          // unfiltered route as the Storefront Pro entry below; the SF and SFP
          // L1s are mutually exclusive per retailer, so this renders exactly
          // once (CXP-212044).
          labelId: 'retailerFundedMarketingDomain.segmentation.nav',
          route: 'retailer-funded-marketing-segmentation-list',
          accessControl: retailerFundedMarketingSegmentationAccessControl,
          positionNavV3: 2,
        },
      ],
    },
    {
      attachToV3: NavEntryPointV3.StorefrontPro,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Create and manage retailer-funded marketing campaigns',
          labelId: 'retailerFundedMarketingDomain.campaigns.phaseFour.nav',
          labelIdV3: 'retailerFundedMarketingDomain.campaigns.phaseFour.nav.v3',
          route: 'retailer-funded-marketing-site-scoped-list-storefront',
          accessControl: retailerFundedMarketingHomeAccessControl,
          positionNavV3: 0,
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage marketing campaigns exclusively for your custom Storefront Pro platform',
          labelId: 'retailerFundedMarketingDomain.storefrontProCampaigns.nav',
          labelIdV3: 'retailerFundedMarketingDomain.storefrontProCampaigns.nav.v3',
          route: 'campaigns-storefront-pro-list',
          subRoutes: [
            'partner-campaigns-create-sfx',
            'partner-campaigns-edit',
            'partner-campaigns-view',
          ],
          accessControl: sfpCampaignAndSegmentAccessControl,
          positionNavV3: 1,
        },
        {
          type: 'item',
          llmDescription: 'Manage lifecycle marketing and customer relationship campaigns',
          labelId: 'retailerFundedMarketingDomain.lifecycleCrm.nav',
          labelIdV3: 'retailerFundedMarketingDomain.lifecycleCrm.nav.v3',
          route: 'partner-scoped-campaigns-lifecycle-crm-storefront-pro',
          accessControl: lifecycleCrmAccessControl,
          positionNavV3: 2,
        },
        {
          type: 'item',
          llmDescription: 'Manage customer segments for Storefront Pro campaigns',
          // Keep the v2 label "Storefront pro segments" so it doesn't collide
          // with the site-scoped "Segments" entry below.
          labelId: 'retailerFundedMarketingDomain.storefrontPro.segmentation.nav',
          route: 'segment-storefront-pro-list',
          accessControl: sfpCampaignAndSegmentAccessControl,
          positionNavV3: 3,
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage customer segments for all retailer-funded marketing campaigns',
          // Site-scoped "Segments" page. Uses the same unfiltered route as the
          // Marketplace L1 entry — the Storefront-only scope filter was removed
          // so SFP retailers see all their existing segments (CXP-212044).
          labelId: 'retailerFundedMarketingDomain.segmentation.nav',
          route: 'retailer-funded-marketing-segmentation-list',
          accessControl: retailerFundedMarketingSegmentationAccessControl,
          positionNavV3: 4,
        },
      ],
    },
    {
      attachToV3: NavEntryPointV3.Marketplace,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            "Manage Instacart's main marketplace (eg: instacart.com) marketing campaigns for product promotions",
          labelId: 'retailerFundedMarketingDomain.marketplaceCampaigns.nav',
          labelIdV3: 'retailerFundedMarketingDomain.marketplaceCampaigns.nav.v3',
          route: 'campaigns-marketplace-list',
          subRoutes: [
            'partner-campaigns-create-mp',
            'partner-campaigns-edit',
            'partner-campaigns-view',
          ],
          accessControl: retailerFundedMarketingWholesalerAccessControl,
          positionNavV3: 1,
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage customer segments specifically for Instacart marketplace (instacart.com) campaigns',
          // Partner-scoped (wholesaler) marketplace segments. Keep the v2 label
          // "Marketplace segments" in v3 so it doesn't collide with the
          // site-scoped "Segments" entry below — both audiences operate under
          // the Marketplace L1.
          labelId: 'retailerFundedMarketingDomain.marketplace.segmentation.nav',
          route: 'segment-marketplace-list',
          accessControl: retailerFundedMarketingWholesalerAccessControl,
          positionNavV3: 2,
        },
      ],
    },
  ],
  adminNavigationEntries: [
    {
      attachToV2: AdminNavEntryPointV2.Marketing,
      attachToV3: AdminNavEntryPointV3.Marketplace,
      subNavItems: [
        {
          route: 'app-admin-global-campaigns-campaign-moments',
          subRoutes: [
            'app-admin-global-campaigns-campaign-moments-create',
            'app-admin-global-campaigns-campaign-moments-edit',
            'app-admin-global-campaigns-campaign-moments-view',
            'app-admin-global-campaigns-campaign-moments-view-moment',
            'campaigns-create',
            'campaigns-view',
            'retailer-funded-marketing-segmentation-view',
          ],
          labelId: 'global.campaigns.title',
          accessControl: retailerFundedMarketingAdminAccessControl,
          positionNavV2: 0,
          positionNavV3: 0,
        },
      ],
    },
    {
      attachToV2: AdminNavEntryPointV2.Marketing,
      attachToV3: AdminNavEntryPointV3.Marketplace,
      subNavItems: [
        {
          route: 'app-admin-campaigns-list',
          subRoutes: [
            'app-admin-campaigns-create',
            'app-admin-campaigns-edit',
            'app-admin-campaigns-get',
            'app-admin-campaigns-templates',
            'app-admin-retailer-funded-marketing-campaign-templates',
            'retailer-funded-marketing-site-scoped-campaign-templates',
          ],
          labelId: 'campaignManagement.customCampaign.title',
          accessControl: retailerFundedMarketingAdminCustomCampaignAccessControl,
          positionNavV2: 4,
          positionNavV3: 1,
        },
      ],
    },
    {
      attachToV2: AdminNavEntryPointV2.Marketing,
      attachToV3: AdminNavEntryPointV3.Marketplace,
      subNavItems: [
        {
          route: 'app-admin-marketing-agent-calendar',
          subRoutes: ['app-admin-marketing-agent-campaigns-view'],
          labelId: 'marketingAgent.title',
          accessControl: marketingAgentCalendarUnionAccessControl,
          positionNavV2: 5,
          positionNavV3: 3,
        },
      ],
    },
  ],
}

export default definition
